import { useLayoutEffect, useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Search,
  Sparkles,
  MapPin,
  Layers,
  ArrowRight,
  Container,
  Key,
  HardHat,
  Shield,
  Palette,
  Briefcase,
  CalendarDays,
  Megaphone,
  Fuel,
  ShoppingCart,
  Users,
  Wrench,
  Scale,
  Stethoscope,
  Hammer,
  Utensils,
  HeartHandshake,
  Truck,
  Dumbbell,
  Headphones,
  Factory,
  Car,
  GraduationCap,
  Camera,
  Hotel,
} from "lucide-react";
import { DEMOS, CATEGORIES } from "@/src/data/demos";
import { operatorSlugFromHostname } from "@/src/lib/operator";
import { resolveOperator } from "@/src/data/operators";
import { useTenantBrand } from "@/src/lib/tenant-brand";
import { VERTICALS } from "@/src/data/verticals";
import { cn } from "@/src/lib/utils";
import { motion } from "motion/react";
import EarlyAccessHero from "@/src/components/EarlyAccessHero";
import bubbleHero from "../assets/sloe-bubble-hero.png";

const LEGACY_INDUSTRY_KEY = "sloe.industry.v1";

const REGIONS = [
  { slug: "all", label: "All markets", flag: "🌐" },
  { slug: "usa", label: "USA", flag: "🇺🇸" },
  { slug: "uk", label: "UK", flag: "🇬🇧" },
  { slug: "south_africa", label: "South Africa", flag: "🇿🇦" },
  { slug: "uae", label: "UAE", flag: "🇦🇪" },
  { slug: "bahrain", label: "Bahrain", flag: "🇧🇭" },
];

function regionCount(demo: typeof DEMOS[number]): number {
  if (!demo.playbook?.regional_variants) return 0;
  return Object.keys(demo.playbook.regional_variants).length;
}

function hasRegion(demo: typeof DEMOS[number], regionSlug: string): boolean {
  if (regionSlug === "all") return true;
  if (!demo.playbook?.regional_variants) return false;
  return regionSlug in demo.playbook.regional_variants;
}

interface PreviewTile {
  demoId: string;
  label: string;
  bgClass: string;
  accentClass: string;
  borderClass: string;
  icon: any;
  style: string;
}

const EXPLORE_PREVIEW_TILES: PreviewTile[] = [
  { demoId: "luxury-design-os",         label: "Luxury design",     bgClass: "bg-[#f5f0e8]",    accentClass: "bg-[#c9a678]",  borderClass: "border-stone-300",         icon: Palette,   style: "editorial" },
  { demoId: "pe-dealflow-os",           label: "PE dealflow",       bgClass: "bg-[#0a0f1e]",    accentClass: "bg-[#4d9fff]",  borderClass: "border-slate-800",         icon: Briefcase, style: "terminal" },
  { demoId: "football-agency-os",       label: "Football agency",   bgClass: "bg-emerald-950",  accentClass: "bg-emerald-400", borderClass: "border-emerald-900",      icon: Sparkles,  style: "sports" },
  { demoId: "realty-os",                label: "Realty",            bgClass: "bg-[#f7f5f1]",    accentClass: "bg-indigo-700",  borderClass: "border-stone-300",        icon: Key,       style: "brokerage" },
  { demoId: "property-management-os",   label: "Property mgmt",     bgClass: "bg-slate-900",    accentClass: "bg-amber-400",   borderClass: "border-slate-800",        icon: HardHat,   style: "ops" },
  { demoId: "academy-intelligence-os",  label: "Academy",           bgClass: "bg-[#fdf8f0]",    accentClass: "bg-[#8b2c3a]",   borderClass: "border-amber-900/15",     icon: Shield,    style: "academy" },
  { demoId: "trade-docs-os",            label: "Trade docs",        bgClass: "bg-[#1a1817]",    accentClass: "bg-amber-400",   borderClass: "border-stone-900",        icon: Container, style: "industrial" },
  { demoId: "social-media-manager-agentic-os", label: "Social media", bgClass: "bg-fuchsia-950", accentClass: "bg-fuchsia-400", borderClass: "border-fuchsia-900", icon: Megaphone, style: "terminal" },
  { demoId: "oil-gas-operations-agentic-os", label: "Oil & gas", bgClass: "bg-stone-950", accentClass: "bg-amber-400", borderClass: "border-stone-800", icon: Fuel, style: "industrial" },
  { demoId: "ecommerce-ops-agentic-os", label: "Ecommerce", bgClass: "bg-sky-950", accentClass: "bg-sky-400", borderClass: "border-sky-900", icon: ShoppingCart, style: "terminal" },
  { demoId: "recruiting-agentic-os", label: "Recruiting", bgClass: "bg-violet-950", accentClass: "bg-violet-400", borderClass: "border-violet-900", icon: Users, style: "terminal" },
  { demoId: "construction-project-agentic-os", label: "Construction", bgClass: "bg-[#21180a]", accentClass: "bg-yellow-400", borderClass: "border-yellow-900/40", icon: Hammer, style: "industrial" },
  { demoId: "logistics-dispatch-agentic-os", label: "Logistics", bgClass: "bg-indigo-950", accentClass: "bg-indigo-400", borderClass: "border-indigo-900", icon: Truck, style: "terminal" },
  { demoId: "creator-business-agentic-os", label: "Creator ops", bgClass: "bg-purple-950", accentClass: "bg-purple-400", borderClass: "border-purple-900", icon: Camera, style: "terminal" },
  { demoId: "field-service-agentic-os", label: "Field service", bgClass: "bg-orange-950", accentClass: "bg-orange-400", borderClass: "border-orange-900", icon: Wrench, style: "terminal" },
  { demoId: "legal-case-agentic-os", label: "Legal cases", bgClass: "bg-slate-950", accentClass: "bg-slate-300", borderClass: "border-slate-800", icon: Scale, style: "terminal" },
  { demoId: "dental-clinic-agentic-os", label: "Dental clinic", bgClass: "bg-cyan-950", accentClass: "bg-cyan-400", borderClass: "border-cyan-900", icon: Stethoscope, style: "terminal" },
  { demoId: "restaurant-ops-agentic-os", label: "Restaurant", bgClass: "bg-red-950", accentClass: "bg-red-400", borderClass: "border-red-900", icon: Utensils, style: "terminal" },
  { demoId: "nonprofit-fundraising-agentic-os", label: "Fundraising", bgClass: "bg-emerald-950", accentClass: "bg-emerald-400", borderClass: "border-emerald-900", icon: HeartHandshake, style: "terminal" },
  { demoId: "insurance-broker-agentic-os", label: "Insurance", bgClass: "bg-blue-950", accentClass: "bg-blue-400", borderClass: "border-blue-900", icon: Shield, style: "terminal" },
  { demoId: "fitness-coaching-agentic-os", label: "Coaching", bgClass: "bg-lime-950", accentClass: "bg-lime-400", borderClass: "border-lime-900", icon: Dumbbell, style: "terminal" },
  { demoId: "saas-customer-success-agentic-os", label: "Customer success", bgClass: "bg-teal-950", accentClass: "bg-teal-400", borderClass: "border-teal-900", icon: Headphones, style: "terminal" },
  { demoId: "manufacturing-quality-agentic-os", label: "Manufacturing", bgClass: "bg-zinc-950", accentClass: "bg-zinc-300", borderClass: "border-zinc-800", icon: Factory, style: "terminal" },
  { demoId: "auto-dealer-agentic-os", label: "Auto dealer", bgClass: "bg-rose-950", accentClass: "bg-rose-400", borderClass: "border-rose-900", icon: Car, style: "terminal" },
  { demoId: "education-cohort-agentic-os", label: "Education", bgClass: "bg-purple-950", accentClass: "bg-purple-400", borderClass: "border-purple-900", icon: GraduationCap, style: "terminal" },
  { demoId: "event-production-agentic-os", label: "Events", bgClass: "bg-pink-950", accentClass: "bg-pink-400", borderClass: "border-pink-900", icon: CalendarDays, style: "terminal" },
  { demoId: "security-guard-ops-agentic-os", label: "Security", bgClass: "bg-neutral-950", accentClass: "bg-neutral-300", borderClass: "border-neutral-800", icon: Shield, style: "terminal" },
  { demoId: "mortgage-broker-agentic-os", label: "Mortgage", bgClass: "bg-green-950", accentClass: "bg-green-400", borderClass: "border-green-900", icon: Key, style: "terminal" },
  { demoId: "short-stay-hospitality-agentic-os", label: "Short-stay", bgClass: "bg-cyan-950", accentClass: "bg-cyan-400", borderClass: "border-cyan-900", icon: Hotel, style: "terminal" },
];

export default function Explore() {
  const pageRef = useRef<HTMLDivElement | null>(null);
  // GUILD: resolve the operator from the branded-door hostname once. When an
  // approved operator matches, its vertical's sub-category chips replace the
  // global CATEGORIES; an unknown host keeps the standard Studio list.
  const operator = resolveOperator(operatorSlugFromHostname());
  // White-label brand for the visible copy (operator company → neutral).
  const brand = useTenantBrand();
  // GUILD: operator doors drop "Featured" (meaningless for a single vertical) —
  // rendered chip list becomes ["All", ...sub-categories]. We exclude it here
  // without mutating VERTICALS. Non-operator hosts keep the full CATEGORIES.
  const categories = operator
    ? VERTICALS[operator.vertical].categories.filter((c) => c !== "Featured")
    : CATEGORIES;
  // GUILD: per-vertical hero copy when on an operator door whose vertical defines it;
  // undefined → non-operator host (or a vertical without hero) keeps the generic copy.
  const heroCopy = operator ? VERTICALS[operator.vertical].hero : undefined;
  // On an operator door, restrict the base demo set to the operator's vertical so
  // "All" never surfaces other verticals. Memoized on the stable `operator` ref.
  const scopedDemos = useMemo(
    () =>
      operator
        ? DEMOS.filter((d) => VERTICALS[operator.vertical].categories.includes(d.category))
        : DEMOS,
    [operator],
  );
  const [activeCategory, setActiveCategory] = useState(operator ? "All" : categories[0]);
  const [activeRegion, setActiveRegion] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showHeroReturn, setShowHeroReturn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToHero = (behavior: ScrollBehavior = "smooth") => {
    pageRef.current?.scrollTo({ top: 0, behavior });
    window.scrollTo({ top: 0, left: 0, behavior });
    setShowHeroReturn(false);
  };

  const scrollToExamples = () => {
    const container = pageRef.current;
    const examples = document.getElementById("examples");
    if (!container || !examples) return;

    const filterBar = document.getElementById("explore-filter-bar");
    const containerTop = container.getBoundingClientRect().top;
    const examplesTop = examples.getBoundingClientRect().top;
    const filterOffset = (filterBar?.getBoundingClientRect().height ?? 120) + 36;
    const targetTop = container.scrollTop + examplesTop - containerTop - filterOffset;

    container.scrollTo({ top: Math.max(0, targetTop), behavior: "smooth" });
  };

  useLayoutEffect(() => {
    const resetScroll = () => {
      scrollToHero("auto");
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    resetScroll();
    const frame = requestAnimationFrame(resetScroll);
    const timer = window.setTimeout(resetScroll, 80);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.history.scrollRestoration = previousRestoration;
    };
  }, [location.key]);

  const filtered = useMemo(() => {
    return scopedDemos.filter((demo) => {
      if (activeCategory === "Featured" && !demo.featured) return false;
      if (activeCategory !== "Featured" && activeCategory !== "All" && demo.category !== activeCategory) return false;
      if (!hasRegion(demo, activeRegion)) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        return demo.name.toLowerCase().includes(q) || demo.tagline.toLowerCase().includes(q);
      }
      return true;
    });
  }, [scopedDemos, activeCategory, activeRegion, searchQuery]);

  const featuredRow = useMemo(() => {
    return scopedDemos.filter((d) => d.featured && hasRegion(d, activeRegion)).slice(0, 3);
  }, [scopedDemos, activeRegion]);

  const hasAnyFilter = activeCategory !== "Featured" || activeRegion !== "all" || searchQuery.trim().length > 0;

  return (
    <div
      ref={pageRef}
      onScroll={(event) => setShowHeroReturn(event.currentTarget.scrollTop > 260)}
      className="flex flex-col h-full overflow-y-auto bg-white"
    >
      <EarlyAccessHero />

      {/* ─── HERO HEADER ─── (P2.2 — Custom Build Service framing) */}
      <div className="relative flex min-h-[860px] sm:min-h-[760px] md:min-h-[620px] w-full items-center overflow-hidden border-b border-white/10 bg-black">
        <div aria-hidden="true" className="absolute inset-0 overflow-hidden opacity-80">
          {[0, 1].map((layer) => (
            <motion.div
              key={layer}
              className="absolute inset-x-0 h-full bg-cover bg-center will-change-transform"
              style={{
                top: `${layer * 100}%`,
                backgroundImage: `url(${bubbleHero})`,
              }}
              animate={{ y: ["0%", "-100%"] }}
              transition={{
                duration: 14,
                ease: "linear",
                repeat: Infinity,
              }}
            />
          ))}
        </div>
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative w-full max-w-7xl mx-auto px-8 py-20 md:py-16">
          <div className="max-w-4xl mx-auto">
            {/* Brand Masthead — mb-7 restored after P2.5 removed the scarcity
                pill that previously sat between this and the headline. */}
            <div className="flex items-center justify-center gap-2 mb-7">
              <div className="w-9 h-9 rounded-lg bg-white/10 border border-white/15 backdrop-blur flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-emerald-400" strokeWidth={2} />
              </div>
              <span className="text-xl font-semibold tracking-tight text-white">
                {brand.name}
              </span>
            </div>

            {/* P2.2 — Hero: Custom Build Service positioning (not template marketplace). */}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white mb-5 leading-[1.15] text-center">
              {heroCopy ? heroCopy.headline : `${brand.name} turns your work into an AI Agentic Application.`}
            </h1>

            <p className="text-base md:text-lg text-white/75 max-w-2xl mx-auto leading-relaxed text-center mb-8">
              {heroCopy ? heroCopy.subcopy : "Answer a few tight questions. The Architect gives you a working application with screens, agents, Decisions, Receipts, and an upgrade path shaped around your role."}
            </p>

            {/* P2.2 — Primary CTA. Visually the largest interactive element above the fold.
                Secondary link smooth-scrolls to the examples grid below. */}
            <div className="flex flex-col items-center gap-3 mb-12">
              <Link
                to="/build"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-slate-950 font-semibold text-base px-8 py-4 rounded-xl transition-colors shadow-sm shadow-black/20 hover:shadow-md"
              >
                Build My Agentic Application
                <ArrowRight className="w-4 h-4" />
              </Link>
              <button
                onClick={scrollToExamples}
                className="text-sm text-white/70 hover:text-white transition-colors"
              >
                or browse Agentic Application examples ↓
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* ─── STICKY FILTER BAR ─── */}
      <div id="explore-filter-bar" className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-slate-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-8 py-4 flex flex-col lg:flex-row gap-3 lg:items-center">
          {showHeroReturn && (
            <button
              onClick={() => scrollToHero()}
              className="flex-shrink-0 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:text-slate-950 hover:border-slate-300 transition-colors"
            >
              ↑ <span className="max-w-[12rem] truncate">{brand.name}</span>
            </button>
          )}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full bg-slate-50 border border-slate-200 rounded-full pl-10 pr-4 py-2 text-sm outline-none"
            />
          </div>
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
            {REGIONS.map((r) => (
              <button
                key={r.slug}
                onClick={() => setActiveRegion(r.slug)}
                className={cn(
                  "flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors border",
                  activeRegion === r.slug ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200"
                )}
              >
                {r.flag} {r.label}
              </button>
            ))}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-8 pb-4 flex items-center gap-2 overflow-x-auto scrollbar-none">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={cn(
                "flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full transition-colors",
                activeCategory === category ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
              )}
            >
              {category}
            </button>
          ))}
          {/* P2.2 — "+ Build Yours" is the active path, not a filter. Emerald-filled to
              break visually from the slate filter chips AND from the slate-900 selected-
              filter pill, signalling "do this" rather than "show that". */}
          <Link
            to="/build"
            className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full bg-emerald-500 hover:bg-emerald-600 text-white transition-colors shadow-sm"
          >
            + Build Yours
          </Link>
        </div>
      </div>

      {/* ─── FEATURED ROW ─── (non-operator hosts only; operator doors drop Featured) */}
      {!operator && !hasAnyFilter && (
        // P2.2 — id="examples" is the corrected smooth-scroll target for the
        // hero's browse link. The larger margin keeps proof copy clear of the
        // sticky filter bar after the jump.
        <div id="examples" className="max-w-7xl mx-auto px-8 pt-16 scroll-mt-40">
          {/* P2.2 — Frame line: examples are PROOF, not a menu. */}
          <div className="text-center mb-8">
            <p className="text-sm font-medium text-slate-700">
              {scopedDemos.length} live Agentic Application examples across real verticals.
            </p>
            <p className="text-sm text-slate-500 mt-1">
              Yours does not need to match a template. The Architect adapts the shell, screens, language, agents, and permissions to your job.
            </p>
          </div>
          <div className="flex items-end justify-between mb-6">
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Featured</h2>
            <p className="text-sm text-slate-500">Click any to inspect the buyer story, then open the live application shell.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {featuredRow.map((demo) => (
              <ExploreCard key={demo.id} demo={demo} onClick={() => navigate(`/demo/${demo.id}`)} />
            ))}
          </div>
        </div>
      )}

      {/* ─── ALL SYSTEMS GRID ─── */}
      <div className="max-w-7xl mx-auto px-8 pt-16 pb-12 w-full">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
            {hasAnyFilter ? `${filtered.length} results` : "All Agentic Application examples"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {!hasAnyFilter && `${scopedDemos.length} Agentic Application examples • Built from real operator workflows and buyer pain points`}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-8">
          {filtered.map((demo) => (
            <ExploreCard key={demo.id} demo={demo} onClick={() => navigate(`/demo/${demo.id}`)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function ExploreCard({ demo, onClick }: { demo: any; onClick: () => void }) {
  const cardRef = useRef<HTMLButtonElement | null>(null);
  const [hasEnteredView, setHasEnteredView] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setHasEnteredView(true);
        }
      },
      { rootMargin: "200px" }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, []);

  const tile = EXPLORE_PREVIEW_TILES.find(t => t.demoId === demo.id);
  const Icon = tile?.icon || Layers;
  const isDark = tile?.style === "terminal" || tile?.style === "sports" || tile?.style === "ops" || tile?.style === "industrial";
  const regions = regionCount(demo);

  return (
    <button ref={cardRef} onClick={onClick} className="group text-left relative flex flex-col bg-white rounded-2xl border border-slate-200 overflow-hidden hover:border-slate-300 hover:shadow-md transition-all">
      <div className={cn("relative aspect-[16/10] overflow-hidden p-4", tile?.bgClass || "bg-slate-50")}>
        <div className="absolute top-4 right-4 z-20">
          <span className="bg-white/90 backdrop-blur-md text-[9px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full text-slate-600 border border-slate-100 shadow-sm">
            {demo.category}
          </span>
        </div>
        
        {hasEnteredView ? (
          <div className="w-full h-full rounded-xl shadow-sm border border-slate-900/5 overflow-hidden flex relative bg-white group-hover:scale-[1.02] transition-transform duration-500">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <iframe
                src={`/?embed=${demo.id}&region=usa`}
                className="border-0"
                style={{
                  width: "1440px",
                  height: "900px",
                  transform: `scale(0.25)`,
                  transformOrigin: "top left",
                }}
                tabIndex={-1}
              />
            </div>
          </div>
        ) : (
          <div className={cn(
            "w-full h-full rounded-xl shadow-sm border overflow-hidden flex relative group-hover:scale-[1.02] transition-transform duration-500",
            isDark ? "border-white/10 bg-black/20" : "border-slate-900/5 bg-white"
          )}>
            <div className={cn("w-[28%] h-full border-r p-3 flex flex-col gap-2", isDark ? "border-white/10 bg-black/25" : "border-slate-100 bg-slate-50")}>
              <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center", tile?.accentClass || "bg-emerald-500")}>
                <Icon className={cn("w-4 h-4", isDark ? "text-slate-950" : "text-white")} />
              </div>
              {demo.modules.slice(0, 4).map((mod: string, index: number) => (
                <div key={mod} className={cn("h-2 rounded-full", index === 0 ? tile?.accentClass || "bg-emerald-500" : isDark ? "bg-white/15" : "bg-slate-200")} />
              ))}
            </div>
            <div className="flex-1 p-3">
              <div className={cn("h-2.5 w-28 rounded-full mb-3", tile?.accentClass || "bg-emerald-500")} />
              <div className="grid grid-cols-2 gap-2">
                {demo.modules.slice(0, 4).map((mod: string) => (
                  <div key={mod} className={cn("rounded-lg border p-2 min-h-12", isDark ? "border-white/10 bg-white/5" : "border-slate-100 bg-slate-50")}>
                    <div className={cn("h-1.5 w-3/4 rounded-full mb-2", isDark ? "bg-white/25" : "bg-slate-300")} />
                    <div className={cn("h-1.5 w-1/2 rounded-full", isDark ? "bg-white/10" : "bg-slate-200")} />
                  </div>
                ))}
              </div>
            </div>
            <span className="absolute left-3 bottom-3 rounded-full bg-white/90 px-2 py-1 text-[8px] font-bold uppercase tracking-widest text-slate-600 shadow-sm">
              Live shell
            </span>
          </div>
        )}
      </div>
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-lg font-semibold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">{demo.name}</h3>
        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2 mb-4">{demo.tagline}</p>
        <div className="mt-auto pt-4 border-t border-slate-100 flex items-center gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
            <span className="flex items-center gap-1.5"><Layers className="w-3 h-3" /> {demo.modules.length} Screens</span>
            <span className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {regions > 0 ? `${regions} Regions` : "Demo Data"}</span>
        </div>
      </div>
    </button>
  );
}
